import { useId, useMemo } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { accountQuery, useAuthSession } from '@/api';
import { useBetslip } from '@/betslip';
import { openChat, useChatUnreadCount } from '@/chat';
import { isChatEnabled } from '@/domain/chat';
import { cn } from '@/lib/cn';
import { Icon } from '@/ui';
import { MobileBetslipPopup } from './MobileBetslipPopup';
import { MOBILE_NAV, type NavItem } from './nav-config';

export interface MobileBottomNavProps {
  className?: string;
  /** When true, omit the in-flow height spacer (shell already reserves the bar). */
  skipSpacer?: boolean;
}

function betslipTabFromSearch(search: Record<string, unknown>): 'betslip' | 'my-bets' {
  return search.tab === 'my-bets' ? 'my-bets' : 'betslip';
}

function isMobileNavActive(
  pathname: string,
  search: Record<string, unknown>,
  item: Pick<NavItem, 'to' | 'search' | 'action'>,
) {
  if (item.action === 'open-chat') return false;
  if (item.to === '/') return pathname === '/';
  if (item.to === '/betslip') {
    if (pathname !== '/betslip') return false;
    const currentTab = betslipTabFromSearch(search);
    const itemTab = item.search?.tab === 'my-bets' ? 'my-bets' : 'betslip';
    return currentTab === itemTab;
  }
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

function BetCountBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  const label = count > 99 ? '99+' : String(count);
  return (
    <span
      aria-hidden
      className="absolute -top-1 -right-1.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-action-primary px-1 text-[9px] font-bold leading-none text-text-on-action"
    >
      {label}
    </span>
  );
}

/** Figma GlowClip — purple ellipse blur, rendered behind the glass surface. */
function ActiveGlow() {
  const filterId = useId().replace(/:/g, '');
  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute top-0 left-1/2 h-full min-h-20 w-[138px] -translate-x-1/2"
      viewBox="0 0 138 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g filter={`url(#${filterId})`}>
        <ellipse cx="69" cy="22" rx="55" ry="32" fill="#7C3AED" fillOpacity="0.7" />
      </g>
      <defs>
        <filter
          id={filterId}
          x="-52.5"
          y="-50"
          width="243"
          height="144"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="20" result="effect1_foregroundBlur" />
        </filter>
      </defs>
    </svg>
  );
}

/** Figma Menu bar: 80px tall, padding 16px 12px 12px. */
const BAR_HEIGHT = 'h-20';

const tabClassName = (isActive: boolean) =>
  cn(
    'group relative flex min-w-0 flex-[1_0_0] flex-col items-center justify-start gap-1',
    isActive ? 'text-text-on-action' : 'text-icon-muted',
  );

export function MobileBottomNav({ className, skipSpacer = false }: MobileBottomNavProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loggedIn } = useAuthSession();
  const { data: account } = useQuery(accountQuery());
  const chatEnabled = isChatEnabled(account);
  const chatUnread = useChatUnreadCount();
  const { pathname, search } = useRouterState({
    select: (state) => ({
      pathname: state.location.pathname,
      search: state.location.search as Record<string, unknown>,
    }),
  });
  const { selectionCount } = useBetslip();

  const items = useMemo(
    () => MOBILE_NAV.filter((item) => item.action !== 'open-chat' || chatEnabled),
    [chatEnabled],
  );

  const activeIndex = items.findIndex((item) => isMobileNavActive(pathname, search, item));

  return (
    <>
      {/* In-flow spacer matching the fixed bar height. */}
      {!skipSpacer ? (
        <div aria-hidden className={cn('w-full shrink-0 lg:hidden', BAR_HEIGHT)} />
      ) : null}

      <nav
        aria-label={t('Main', { defaultValue: 'Main' })}
        className={cn('fixed inset-x-0 bottom-0 z-30 w-full lg:hidden', className)}
      >
        <MobileBetslipPopup />
        <div className={cn('relative overflow-hidden rounded-t-[12px]', BAR_HEIGHT)}>
          {/* 1. Glow behind glass */}
          <div aria-hidden className="pointer-events-none absolute inset-0 z-0 flex px-3">
            {items.map((item, index) => (
              <div key={item.translateKey} className="relative flex-1">
                {index === activeIndex ? <ActiveGlow /> : null}
              </div>
            ))}
          </div>

          {/* 2. GlassSurface */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 z-[1] rounded-t-[12px] border-t border-white/10 bg-[rgba(11,18,31,0.72)] backdrop-blur-[10px]"
          />

          {/* 3. Tabs */}
          <div className="relative z-[2] flex h-full w-full items-start justify-between px-3 pt-4 pb-3">
            {items.map((item, index) => {
              const isBetslip = item.to === '/betslip' && item.search?.tab !== 'my-bets';
              const isMyBets = item.to === '/betslip' && item.search?.tab === 'my-bets';
              const isChat = item.action === 'open-chat';
              const badgeCount = isBetslip ? selectionCount : isChat ? chatUnread : 0;
              const isActive = index === activeIndex;
              const label = t(item.translateKey, { defaultValue: item.label });

              if (isChat) {
                return (
                  <button
                    key={item.translateKey}
                    type="button"
                    aria-label={
                      badgeCount > 0
                        ? t('ChatWithUnread', {
                            defaultValue: 'Chat, {{count}} unread',
                            count: badgeCount,
                          })
                        : label
                    }
                    onClick={() => {
                      // Call the controller directly — more reliable than only
                      // relying on the sportsbook:open-chat window event.
                      openChat();
                    }}
                    className={tabClassName(false)}
                  >
                    <span className="relative flex size-6 shrink-0 items-center justify-center overflow-visible">
                      <Icon name={item.icon} size={24} />
                      <BetCountBadge count={badgeCount} />
                    </span>
                    <span className="line-clamp-2 w-full px-0.5 text-center text-[9px] leading-tight break-words">
                      {label}
                    </span>
                  </button>
                );
              }

              return (
                <Link
                  key={item.translateKey}
                  to={item.to}
                  search={item.search}
                  activeOptions={{ exact: item.to === '/' }}
                  aria-label={
                    isBetslip && badgeCount > 0
                      ? t('BetslipWithCount', {
                          defaultValue: 'Bet Slip, {{count}} selections',
                          count: badgeCount,
                        })
                      : undefined
                  }
                  onClick={(event) => {
                    if (!isMyBets || loggedIn) return;
                    event.preventDefault();
                    void navigate({
                      to: '/login',
                      search: { redirect: '/betslip?tab=my-bets' },
                    });
                  }}
                  className={tabClassName(isActive)}
                >
                  <span className="relative flex size-6 shrink-0 items-center justify-center overflow-visible">
                    <Icon name={item.icon} size={24} />
                    <BetCountBadge count={badgeCount} />
                  </span>
                  <span className="line-clamp-2 w-full px-0.5 text-center text-[9px] leading-tight break-words">
                    {label}
                  </span>
                  <span
                    aria-hidden
                    className={cn(
                      'absolute -top-4 left-1/2 h-1 w-10 -translate-x-1/2 rounded-[2px]',
                      isActive
                        ? 'bg-action-primary shadow-[0px_6px_10px_0px_rgba(124,58,237,0.85)]'
                        : 'bg-transparent',
                    )}
                  />
                </Link>
              );
            })}
          </div>
        </div>
      </nav>
    </>
  );
}
